<template>
  <div class="settings-page type-edition">
    <div class="title-row">
      <h2>
        {{
          mode === "creation" && !typeTitle
            ? "Create type"
            : typeTitle || "Update type"
        }}
      </h2>
      <span v-if="id" class="table-id">
        {{ id }}
        <button class="copy-btn" title="Copy table ID" @click="copyTableId">
          <CopyIcon />
        </button>
      </span>
    </div>
    <div class="content">
      <div class="schema-tab">
        <div class="general">
          <div ref="iconContainer" class="icon">
            <FormInputGroup label="Icon" for-id="base-type">
              <BaseType
                :title="typeTitle"
                :image="selectedEmoji || defaultTypeIcon"
                :color="getTypeColor(database)"
                state="img"
                size="xl"
                :editable="!readonlyType"
                :class="{ readonly: readonlySchema }"
                @image-edit-click="onIconClick"
              />
            </FormInputGroup>
            <div ref="emojiPickerContent" class="emoji-picker-container">
              <BaseEmojiPicker
                :class="{ readonly: readonlyType }"
                :show-search="true"
                :show-skin-tones="true"
                :show-preview="true"
                :show="emojiPickerMenuShown"
                :auto-focus="true"
                @select="onEmojiSelect"
                @close="onEmojiPickerClose"
              />
            </div>
          </div>
          <div class="names">
            <FormInputGroup label="Name" for-id="name">
              <FormInput
                id="name"
                ref="titleInputEl"
                :class="{ readonly: readonlyType }"
                v-model:value="typeTitle"
                type="text"
                placeholder="Untitled"
                :state="readonlyType ? 'disabled' : 'default'"
              />
            </FormInputGroup>
            <FormInputGroup label="Database name" for-id="database-name">
              <FormInput
                id="database-name"
                :class="{ readonly: readonlyType }"
                v-model:value="databaseInput"
                type="text"
                placeholder="Name"
                :state="
                  nameError ? 'error' : readonlyType ? 'disabled' : 'default'
                "
              />
              <BaseAlert v-if="nameError" state="error">
                {{ nameError }}
              </BaseAlert>
            </FormInputGroup>
          </div>
        </div>
        <FormInputGroup label="Description" for-id="type-description">
          <FormInput
            id="type-description"
            :class="{ readonly: readonlyType }"
            v-model:value="typeDescription"
            type="text"
            placeholder="Describe the purpose of this table..."
            :state="readonlyType ? 'disabled' : 'default'"
          />
        </FormInputGroup>
        <div class="properties">
          <div class="column-headers item">
            <span class="handle-spacer" />
            <span class="column-1">Property</span>
            <span class="column-2">Type</span>
            <!-- <span v-if="hasSystemProperties" class="column-3">Constraints</span> -->
          </div>
          <BaseReorderableList
            :data="propertySections"
            item-key="id"
            handle-selector=".handle"
            @update:data="onPropertiesReorder"
          >
            <template #item="{ index }">
              <div class="item property-row">
                <BaseButton kind="auxiliary" class="handle">
                  <template #icon>
                    <HandleIcon />
                  </template>
                </BaseButton>
                <Definition
                  :name="properties[index].name"
                  :type="properties[index].type"
                  :readonly="
                    readonlySchema ||
                    (!OVERRIDE_TYPE_EDITION &&
                      properties[index].permissions.schema != 'read_write' &&
                      !isContextProperty(properties[index]))
                  "
                  :no-edit="isContextProperty(properties[index])"
                  :index="index"
                  :has-duplicate-name="
                    duplicateFieldNames.includes(properties[index].name)
                  "
                  :is-title-field="properties[index].id === titleFieldId"
                  :is-notes-field="properties[index].id === notesFieldId"
                  :is-hidden="hiddenColumns.has(properties[index].id)"
                  :is-integration="isIntegrationDatabase"
                  :show-system-tag="properties[index].origin === `virtual`"
                  :show-locked-tag="
                    properties[index].permissions.schema === 'read_only' &&
                    properties[index].origin !== 'virtual'
                  "
                  @delete="deleteProperty(index)"
                  @edit="editProperty(index)"
                  @set-title="setTitleField(index)"
                  @set-notes="toggleNotesField(index)"
                  @toggle-hidden="toggleHiddenColumn(index)"
                />
              </div>
            </template>
          </BaseReorderableList>
          <SearchButtonMenu
            class="type"
            list-item-name-property="text"
            :list="addPropertyMenuList"
            :is-disabled="readonlySchema"
            :offset="[0, 4]"
            placement="bottom-start"
            :width="280"
            @selected-item="onAddPropertyMenuSelect"
          >
            <template #btn>
              <BaseButton
                kind="auxiliary"
                text="Add Property"
                class="add"
                :class="{ readonly: readonlySchema }"
                :state="readonlySchema ? 'disabled' : 'default'"
              >
                <template #icon>
                  <AddIcon />
                </template>
              </BaseButton>
            </template>
            <template #item="{ id }">
              <BaseButton
                kind="auxiliary"
                :text="addPropertyMenuRowById(id)?.text ?? ''"
                is-expanded
              >
                <template #icon>
                  <component
                    :is="addPropertyMenuRowById(id)?.definitionType.icon"
                  />
                </template>
              </BaseButton>
            </template>
          </SearchButtonMenu>
          <BaseAlert v-if="propertyError" state="error">
            {{ propertyError }}
          </BaseAlert>
        </div>
        <BaseAlert v-if="guardedTypeError" state="warning">
          {{ guardedTypeError }}
        </BaseAlert>
        <div class="buttons">
          <div class="buttons-left">
            <BaseButton
              kind="main"
              type="primary"
              :state="saveButtonState"
              :text="mode === 'creation' ? 'Create' : 'Save'"
              @click="validate"
            />
            <BaseButton
              v-if="mode === 'edition' && isDirty"
              kind="main"
              type="tertiary"
              text="Cancel"
              @click="cancel"
            >
            </BaseButton>
          </div>
          <div class="buttons-right">
            <BaseButton
              v-if="
                mode === 'edition' &&
                id &&
                canDeleteType &&
                !guardedTypeKind &&
                !isProtectedBuiltinType
              "
              kind="main"
              type="tertiary"
              text="Delete"
              tint="red"
              @click="deleteType"
            >
              <template #icon>
                <DeleteIcon />
              </template>
            </BaseButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { cloneDeep, isEqual } from "lodash-es";
import pluralize from "pluralize";
import { match } from "ts-pattern";
import {
  computed,
  defineComponent,
  h,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  shallowRef,
  toRefs,
  watch,
  type Component,
} from "vue";
import { useModal } from "vue-final-modal";
import { onBeforeRouteLeave } from "vue-router";
import { useTippy } from "vue-tippy";

import {
  CalendarColor,
  CellSchemaRelation,
  DatabaseMetadata,
  FieldWithOriginDictionary,
  GraphCustomType,
  GraphCustomTypeFieldDictionary,
  GraphDatabase,
  GraphDatabaseDictionary,
  GraphRelationTarget,
  IntegrationAppWorkspaceConfiguration,
  ReleasableRef,
  TableId,
} from "@ctrl/controller-rpc";

import { copy } from "@/clipboard";
import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseEmojiPicker from "@/components/base/BaseEmojiPicker.vue";
import BaseReorderableList from "@/components/base/BaseReorderableList.vue";
import BaseType from "@/components/base/BaseType.vue";
import {
  getTypeColor,
  getTypeIcon,
  isBuiltinDatabase,
} from "@/components/databases";
import SearchButtonMenu from "@/components/elements/SearchButtonMenu.vue";
import { graphRecordToProperties } from "@/components/graph/component";
import { contextFieldName } from "@/components/graph/context";
import CreateBaseProperty from "@/components/graph/definition/CreateBaseProperty.vue";
import CreateRelation from "@/components/graph/definition/CreateRelation.vue";
import CreateSelect from "@/components/graph/definition/CreateSelect.vue";
import Definition from "@/components/graph/definition/Definition.vue";
import FormInput from "@/components/graph/definition/form/FormInput.vue";
import FormInputGroup from "@/components/graph/definition/form/FormInputGroup.vue";
import {
  availableDefinitionTypes,
  boolDefinitionType,
  contextDefinitionType,
  dateDefinitionType,
  DefinitionType,
  emailDefinitionType,
  extractOptionalFromCustom,
  isContextProperty,
  multiSelectDefinitionType,
  notesDefinitionType,
  numberDefinitionType,
  phoneDefinitionType,
  PropertyType,
  relationDefinitionType,
  selectDefinitionType,
  stringDefinitionType,
  uriDefinitionType,
} from "@/components/graph/definition/graph-definition";
import {
  persistSelectConfig,
  storedSelectConfig,
} from "@/components/graph/grouping";
import {
  applyPropertyOrder,
  persistPropertyOrder,
  storedPropertyOrder,
} from "@/components/graph/properties";
import {
  builtinTypeKindOf,
  isProtectedBuiltinTable,
  matchesBuiltinTypeSchema,
  provisionedKindOf,
  provisionedSelectConfig,
  type BuiltinTypeKind,
  type ProvisionedTypeKind,
} from "@/components/graph/provisioned";
import {
  getTitleFieldId,
  getTitleFieldIdFromProperties,
} from "@/components/graph/title";
import AddIcon from "@/components/icons/AddIcon.vue";
import CopyIcon from "@/components/icons/CopyIcon.vue";
import DeleteIcon from "@/components/icons/DeleteIcon.vue";
import HandleIcon from "@/components/icons/HandleIcon.vue";
import TicketIcon from "@/components/icons/TicketIcon.vue";
import * as inject from "@/components/inject";
import Modal from "@/components/modals/Modal.vue";
import { dataCollectionTableId } from "@/components/settings/integrations/dataCollection";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { isWorkspaceWritable } from "@/composables/useProfile";
import { RouteNames } from "@/routes/types";
import {
  DELETE_TYPE_CONFIRMATION_COPY,
  requestDeleteConfirmation,
} from "@/utils/confirmation";
import { navigateTo } from "@/utils/navigate";

import CloseIcon from "../icons/CloseIcon.vue";

// Temporary kill switch that lifts the provisioned-type guard (Delete
// button hidden, breaking edits rejected on Save, locked provisioned
// columns not editable) so provisioned types can be tampered with during
// development. Must stay `false` when shipping.
const OVERRIDE_TYPE_EDITION = false;

type ModalPropertyAttributes = {
  mode: "create" | "edit";
  index: number;
  name: string;
  description: string;
  selectOptions: Array<GraphCustomType>;
  // Keyed by option object identity (option values are editable in the
  // modal, so a value-keyed record would detach on rename); materialized to
  // a value-keyed record when the modal is submitted.
  selectColors: Map<GraphCustomType, CalendarColor>;
  relation?: CellSchemaRelation;
  filled: Filled;
  optional: boolean;
  list: boolean;
};
export type Filled = { name: boolean; relation: boolean };

export default defineComponent({
  components: {
    SearchButtonMenu,
    AddIcon,
    BaseAlert,
    BaseButton,
    BaseEmojiPicker,
    BaseReorderableList,
    BaseType,
    HandleIcon,
    CopyIcon,
    Definition,
    DeleteIcon,
    CloseIcon,
    FormInput,
    FormInputGroup,
  },
  props: {
    id: { type: String, required: false },
    orgId: { type: String, required: true },
    workspaceId: { type: String, required: true },
  },
  async setup(props) {
    const { id, orgId, workspaceId: currentWorkspaceId } = toRefs(props);
    const router = inject.router();
    const controller = inject.controller();
    const simpleModal = inject.simpleModal();
    const profile = inject.profile();
    const browserStore = useBrowserStore(profile.value.user.accountId);
    inject.currentWorkspaceId(currentWorkspaceId);
    const workspaceDatabases = inject.workspaceDatabases();

    const skipRouteGuard = ref(false);
    const pendingDirty = ref(false);

    onBeforeRouteLeave((_to, _from, next) => {
      if (skipRouteGuard.value || !pendingDirty.value) {
        next();
        return;
      }
      const { open } = useModal({
        component: Modal,
        attrs: {
          title: "Unsaved changes",
          showCancelButton: true,
          modalButtons: [
            {
              title: "Discard",
              kind: "main",
              func: async () => {
                next();
              },
            },
          ],
        },
        slots: {
          default: () =>
            h(
              "p",
              "Are you sure you want to leave? Your changes will be lost."
            ),
        },
      });
      open();
    });

    const titleInputEl = ref<InstanceType<typeof FormInput>>();
    onMounted(() => {
      if (mode.value === "creation") {
        titleInputEl.value?.focus();
      }
    });
    const database = computed<GraphDatabase | undefined>(() =>
      id.value && currentWorkspaceId.value && workspaceDatabases.value
        ? workspaceDatabases.value[currentWorkspaceId.value]?.[id.value]
        : undefined
    );
    // Built-in types show their kind icon — the same one the settings list
    // and the console use — over any meta emoji (like the list does). Custom
    // types keep their meta emoji, or the generic type cube when unset.
    const defaultTypeIcon = computed<Component | string>(() =>
      getTypeIcon(profile.value, database.value)
    );
    const mode = computed<"creation" | "edition">(() =>
      id.value ? "edition" : "creation"
    );
    const typeTitle = ref("");
    const typeDescription = ref("");
    const databaseNameToUpdate = ref("");
    const selectedEmoji = ref<string>("");

    watch(
      [database, mode],
      ([database, mode]) => {
        typeTitle.value = mode === "creation" ? "" : database?.data_name || "";
        databaseNameToUpdate.value =
          mode === "creation" ? "" : database?.name || "";
        selectedEmoji.value = "";
        typeDescription.value =
          mode === "creation" ? "" : database?.description || "";
      },
      { immediate: true, deep: true }
    );

    const databaseNameSuggestion = computed<string>(() =>
      pluralize(typeTitle.value.trim().replaceAll(" ", "-").toLowerCase())
    );

    const databaseInput = computed({
      get: () => databaseNameToUpdate.value || databaseNameSuggestion.value,
      set: (dbInput: string) => (databaseNameToUpdate.value = dbInput),
    });

    const types = computed<PropertyType[]>(() =>
      database.value?.type ? graphRecordToProperties(database.value.type) : []
    );

    const relationTargetsRef = ref<GraphRelationTarget[]>();
    controller.graph
      .relationTargets(currentWorkspaceId.value)
      .then((targets) => {
        relationTargetsRef.value = targets;
      });

    const relationTargets = computed<GraphRelationTarget[]>(
      () => relationTargetsRef.value || []
    );

    const readonlySchema = computed<boolean>(
      () => database.value?.permissions.can_update_schema == false
    );
    const readonlyType = computed<boolean>(
      () => database.value?.permissions.can_update_type == false
    );
    // A `reader` member of the workspace cannot change its schema: Save
    // (and Create) stay disabled, and Delete is withheld.
    const canWriteWorkspace = computed(() =>
      isWorkspaceWritable(profile.value, currentWorkspaceId.value)
    );

    // Types synced from a Membrane integration are read-only (the backend sets
    // `can_update_type = false`). Disabling the last data collection backing
    // such a type normally deletes it, but an orphaned type can linger (e.g. a
    // failed disable). We let the user delete those orphans by loading the
    // workspace's Membrane config and checking whether any enabled data
    // collection still points at this type.
    const isIntegrationType = computed<boolean>(
      () => database.value?.origin === "integration_app"
    );
    const integrationConfig = shallowRef<
      ReleasableRef<IntegrationAppWorkspaceConfiguration | null> | undefined
    >();
    let integrationConfigReleased = false;
    controller.integrationApp
      .get(currentWorkspaceId.value)
      .then((configRef) => {
        // The component may have unmounted before the RPC resolved.
        if (integrationConfigReleased) configRef.release();
        else integrationConfig.value = configRef;
      });
    onBeforeUnmount(() => {
      integrationConfigReleased = true;
      integrationConfig.value?.release();
    });
    const hasEnabledDataCollection = computed<boolean>(() => {
      const config = integrationConfig.value?.value;
      if (!config || !id.value) return false;
      return config.enabled_data_collections.some(
        ([, dataCollection]) =>
          dataCollectionTableId(dataCollection) === id.value
      );
    });
    const canDeleteType = computed<boolean>(() => {
      if (!canWriteWorkspace.value) return false;
      if (!readonlyType.value) return true;
      // Only override the read-only lock once we have confirmed (config
      // loaded) that no data collection still backs this integration type —
      // otherwise the Delete button would flash before the config loads.
      return (
        isIntegrationType.value &&
        integrationConfig.value !== undefined &&
        !hasEnabledDataCollection.value
      );
    });

    const properties = ref<PropertyType[]>([]);

    watch(
      [types, mode],
      async ([types, mode]) => {
        if (mode === "creation") {
          const titleId = await controller.graph.database.generateColumnId();
          const notesId = await controller.graph.database.generateColumnId();
          properties.value = [
            {
              id: titleId,
              name: "Title",
              permissions: { schema: "read_only" },
              type: stringDefinitionType.type,
              origin: "table",
              optional: false,
              list: false,
            },
            {
              id: notesId,
              name: "Notes",
              type: notesDefinitionType.type,
              permissions: { schema: "read_write" },
              optional: true,
              origin: "table",
              list: false,
            },
          ];
        } else {
          // Deep-cloned draft: the PropertyType objects built by
          // `graphRecordToProperties` alias the field schemas of the
          // controller's cached database object. The page edits properties
          // in place, so it must own an isolated copy — the new schema only
          // replaces the controller's on Save (see `validate`). The deep
          // watch still resets the draft whenever the database genuinely
          // changes under us.
          properties.value = applyPropertyOrder(
            cloneDeep(types),
            storedPropertyOrder(
              browserStore,
              currentWorkspaceId.value,
              id.value
            ),
            (property) => property.id
          );
        }
      },
      { immediate: true, deep: true }
    );

    const propertySections = computed(() => [{ items: properties.value }]);

    const persistCurrentPropertyOrder = (tableId: string | undefined) => {
      const wsId = currentWorkspaceId.value;
      if (!wsId || !tableId) return;
      persistPropertyOrder(
        browserStore,
        wsId,
        tableId,
        properties.value.map((property) => property.id)
      );
    };

    const onPropertiesReorder = () => {
      persistCurrentPropertyOrder(id.value);
    };

    type TypeEditionSnapshot = {
      title: string;
      description: string;
      databaseName: string;
      emoji: string;
      titleFieldId: string | undefined;
      notesFieldId: string | undefined;
      hiddenColumns: string[];
      properties: Array<{
        id: string;
        name: string;
        description?: string;
        type: PropertyType["type"];
        optional: boolean;
        list: boolean;
        /** Effective select-option colors: they live outside the schema
         * (staged in `pendingSelectColors`, persisted to the browser store
         * on save), so the dirty check must track them explicitly. */
        selectColors: Array<[option: string, color: CalendarColor]>;
      }>;
    };

    const initialSnapshot = ref<TypeEditionSnapshot | null>(null);

    const makeSnapshot = (): TypeEditionSnapshot => ({
      title: typeTitle.value,
      description: typeDescription.value,
      databaseName: databaseInput.value,
      emoji: selectedEmoji.value || database.value?.meta?.emoji || "",
      titleFieldId: titleFieldId.value,
      notesFieldId: notesFieldId.value,
      hiddenColumns: Array.from(hiddenColumns.value).sort(),
      properties: properties.value.map((p) => ({
        id: p.id,
        name: p.name,
        description: p.description,
        type: p.type,
        optional: p.optional ?? true,
        list: p.list,
        selectColors: effectiveSelectColors(p),
      })),
    });

    watch(
      [id, mode],
      () => {
        initialSnapshot.value = null;
      },
      { immediate: true }
    );

    watch(
      [database, mode, types],
      async ([database, mode]) => {
        if (mode !== "edition" || !database || initialSnapshot.value) return;
        await nextTick();
        initialSnapshot.value = cloneDeep(makeSnapshot());
      },
      { immediate: true }
    );

    const isDirty = computed<boolean>(() => {
      if (mode.value === "creation") return true;
      if (!initialSnapshot.value) return false;
      return !isEqual(makeSnapshot(), initialSnapshot.value);
    });

    watch(
      [isDirty, mode, typeTitle],
      ([dirty, m, title]) => {
        pendingDirty.value = m === "creation" ? title.trim().length > 0 : dirty;
      },
      { immediate: true }
    );

    const saveButtonState = computed(() => {
      if (!canWriteWorkspace.value) return "disabled";
      if (mode.value === "edition" && !isDirty.value) return "disabled";
      return "default";
    });

    const defaultModalPropertyAttributes: ModalPropertyAttributes = {
      mode: "create",
      index: 0,
      name: "",
      description: "",
      selectOptions: [],
      selectColors: new Map(),
      relation: undefined,
      filled: { name: true, relation: true },
      optional: true,
      list: false,
    };

    const modalPropertyAttributes = ref<ModalPropertyAttributes>(
      defaultModalPropertyAttributes
    );

    const resetAttributes = () =>
      (modalPropertyAttributes.value = cloneDeep(
        defaultModalPropertyAttributes
      ));

    // Helper function to get duplicate field names
    const duplicateFieldNames = computed(() => {
      const nameCount = new Map<string, number>();

      properties.value.forEach((prop) => {
        const count = nameCount.get(prop.name) || 0;
        nameCount.set(prop.name, count + 1);
      });

      return Array.from(nameCount.entries())
        .filter(([_, count]) => count > 1)
        .map(([name]) => name);
    });

    // Get existing property names for duplicate validation
    const existingPropertyNames = computed(() => {
      const names = properties.value.map((prop) => prop.name);
      // If editing, exclude the current property name
      if (modalPropertyAttributes.value.mode === "edit") {
        const currentIndex = modalPropertyAttributes.value.index;
        return names.filter((_, index) => index !== currentIndex);
      }
      return names;
    });

    // Helper function to check if current name is duplicate
    const isNameDuplicate = () => {
      return (
        modalPropertyAttributes.value.name &&
        existingPropertyNames.value.includes(modalPropertyAttributes.value.name)
      );
    };

    const hasContextProperty = computed(() =>
      properties.value.some(isContextProperty)
    );

    const isBuiltinType = computed(
      () => database.value !== undefined && isBuiltinDatabase(database.value)
    );

    const isIntegrationDatabase = computed(
      () => database.value?.origin === "integration_app"
    );

    const filteredDefinitionTypes = computed(() =>
      hasContextProperty.value || isBuiltinType.value
        ? availableDefinitionTypes.filter((t) => t !== contextDefinitionType)
        : availableDefinitionTypes
    );

    const addPropertyMenuList = computed(() =>
      filteredDefinitionTypes.value.map((definitionType, index) => ({
        id: `add-property-${index}`,
        text: definitionType.text,
        definitionType,
      }))
    );

    const addPropertyMenuRowById = (id: string) =>
      addPropertyMenuList.value.find((r) => r.id === id);

    const createProperty = (definitionType: DefinitionType) => {
      resetAttributes();

      // Multi-selects and relations hold several values by default — for
      // relations the "Limit to 1" toggle starts off.
      if (
        definitionType === multiSelectDefinitionType ||
        definitionType === relationDefinitionType
      ) {
        modalPropertyAttributes.value.list = true;
      }

      if (definitionType === contextDefinitionType) {
        propertyError.value = null;
        if (hasContextProperty.value) {
          propertyError.value = "This type already has a Context property.";
          return;
        }
        const hasContextName = properties.value.some(
          (p) => p.name === contextFieldName
        );
        if (hasContextName) {
          propertyError.value = 'A property named "Context" already exists.';
          return;
        }
        (async () => {
          const id = await controller.graph.database.generateColumnId();
          properties.value.push({
            id,
            name: contextFieldName,
            permissions: { schema: "read_only" },
            origin: "table",
            type: contextDefinitionType.type,
            optional: true,
            list: false,
          });
        })();
        return;
      }

      switch (definitionType.type.type) {
        case stringDefinitionType.type.type:
          switch (
            "format" in definitionType.type ? definitionType.type.format : null
          ) {
            case "email":
              addEmailProperty();
              break;
            case "phone":
              addPhoneProperty();
              break;
            case "uri":
              addUriProperty();
              break;
            default:
              addTextProperty();
              break;
          }
          break;
        case boolDefinitionType.type.type:
          addBoolProperty();
          break;
        case numberDefinitionType.type.type:
          addIntProperty();
          break;
        case dateDefinitionType.type.type:
          addDateProperty();
          break;
        case selectDefinitionType.type.type:
          addSelectProperty();
          break;
        case relationDefinitionType.type.type:
          addRelationProperty();
          break;
        case notesDefinitionType.type.type:
          addNotesProperty();
          break;
      }
    };

    const onAddPropertyMenuSelect = (payload: {
      id: string;
      index: number;
    }) => {
      const row = addPropertyMenuRowById(payload.id);
      if (row) createProperty(row.definitionType);
    };

    const getModalTitle = (baseType: string) => {
      if (baseType === "Select") {
        const type = modalPropertyAttributes.value.list
          ? "Multi Select"
          : "Select";
        return modalPropertyAttributes.value.mode === "edit"
          ? `Edit ${type}`
          : `Create ${type}`;
      }
      return modalPropertyAttributes.value.mode === "edit"
        ? `Edit ${baseType}`
        : `Create ${baseType}`;
    };

    const editProperty = (index: number) => {
      if (properties.value.length >= index + 1) {
        const currentProperty = properties.value[index];
        const typeWithOptional = extractOptionalFromCustom(
          currentProperty.type
        );

        // Deep-cloned: the modal edits options in place, and cancelling
        // it must leave the draft property untouched (only Done writes
        // back into `properties`).
        const selectOptions =
          typeWithOptional.type === "variant"
            ? cloneDeep(typeWithOptional.cases)
            : [];
        // Colors picked on a previous save (or pending from an earlier
        // modal round in this editing session), overlaid on the provisioned
        // defaults (e.g. the Task type's Status), rebound to the cloned
        // option objects.
        const storedColors = {
          ...(database.value
            ? provisionedSelectConfig(database.value, currentProperty.name)
                ?.colors
            : undefined),
          ...(pendingSelectColors.get(currentProperty.id) ??
            (currentWorkspaceId.value && id.value
              ? storedSelectConfig(
                  browserStore,
                  currentWorkspaceId.value,
                  id.value,
                  currentProperty.id
                )?.colors
              : undefined)),
        };
        const selectColors = new Map<GraphCustomType, CalendarColor>();
        for (const option of selectOptions) {
          if (option.type !== "string_const") continue;
          const color = storedColors[option.value];
          if (color) selectColors.set(option, color);
        }

        modalPropertyAttributes.value = {
          mode: "edit",
          index: index,
          name: currentProperty.name,
          description: currentProperty.description || "",
          optional:
            typeWithOptional.optional !== undefined
              ? typeWithOptional.optional
              : true,
          selectOptions,
          selectColors,
          relation:
            typeWithOptional.type === "relation" ? typeWithOptional : undefined,
          filled: { name: true, relation: true },
          list: currentProperty.list,
        };

        // Open the appropriate modal
        match(typeWithOptional)
          .with({ type: "string", format: "email" }, () => {
            addEmailProperty();
          })
          .with({ type: "string", format: "phone" }, () => {
            addPhoneProperty();
          })
          .with({ type: "string", format: "uri" }, () => {
            addUriProperty();
          })
          .with({ type: "string" }, () => {
            addTextProperty();
          })
          .with({ type: "int" }, () => {
            addIntProperty();
          })
          .with({ type: "date" }, () => {
            addDateProperty();
          })
          .with({ type: "bool" }, () => {
            addBoolProperty();
          })
          .with({ type: "variant" }, () => {
            addSelectProperty();
          })
          .with({ type: "relation" }, () => {
            addRelationProperty();
          })
          .with({ type: "note" }, () => {
            addNotesProperty();
          });
      }
    };

    const deleteProperty = (index: number) => {
      if (properties.value.length >= index + 1) {
        properties.value.splice(index, 1);
      }
    };

    // Helper function to create string-based property modals
    const createStringPropertyModal = (
      typeName: "Text" | "Email" | "Phone" | "URL",
      definitionType: DefinitionType
    ) => {
      const handleSubmit = async () => {
        modalPropertyAttributes.value.filled = {
          ...modalPropertyAttributes.value.filled,
          name: !!modalPropertyAttributes.value.name,
        };
        if (modalPropertyAttributes.value.name && !isNameDuplicate())
          if (modalPropertyAttributes.value.mode === "edit") {
            properties.value[modalPropertyAttributes.value.index].name =
              modalPropertyAttributes.value.name;
            properties.value[modalPropertyAttributes.value.index].description =
              modalPropertyAttributes.value.description || undefined;
          } else {
            const id = await controller.graph.database.generateColumnId();
            properties.value.push({
              id,
              name: modalPropertyAttributes.value.name,
              description:
                modalPropertyAttributes.value.description || undefined,
              type: definitionType.type,
              permissions: { schema: "read_write" },
              origin: "table",
              optional: modalPropertyAttributes.value.optional,
              list: modalPropertyAttributes.value.list,
            });
          }
        else return false;
      };

      const modalInstance = useModal({
        component: Modal,
        attrs: {
          title: getModalTitle(typeName),
          submitOnModEnter: true,
          modalButtons: [
            {
              title: "Done",
              kind: "main",
              func: handleSubmit,
            },
          ],
        },
        slots: {
          default: () =>
            h(CreateBaseProperty, {
              name: modalPropertyAttributes.value.name,
              description: modalPropertyAttributes.value.description,
              filled: modalPropertyAttributes.value.filled,
              optional: modalPropertyAttributes.value.optional,
              type: typeName,
              existingNames: existingPropertyNames.value,
              onUpdateName(t: string) {
                modalPropertyAttributes.value.name = t;
              },
              onUpdateDescription(d: string) {
                modalPropertyAttributes.value.description = d;
              },
              onUpdateOptional(o: boolean) {
                modalPropertyAttributes.value.optional = o;
              },
              onSetNameFilled() {
                modalPropertyAttributes.value.filled = {
                  ...modalPropertyAttributes.value.filled,
                  name: true,
                };
              },
              async onSubmit() {
                const result = await handleSubmit();
                if (result !== false) {
                  modalInstance.close();
                }
              },
            }),
        },
      });

      return modalInstance;
    };

    const { open: addTextProperty } = createStringPropertyModal(
      "Text",
      stringDefinitionType
    );

    const { open: addEmailProperty } = createStringPropertyModal(
      "Email",
      emailDefinitionType
    );

    const { open: addPhoneProperty } = createStringPropertyModal(
      "Phone",
      phoneDefinitionType
    );

    const { open: addUriProperty } = createStringPropertyModal(
      "URL",
      uriDefinitionType
    );

    const { open: addBoolProperty } = (() => {
      const handleSubmit = async () => {
        modalPropertyAttributes.value.filled = {
          ...modalPropertyAttributes.value.filled,
          name: !!modalPropertyAttributes.value.name,
        };
        if (modalPropertyAttributes.value.name && !isNameDuplicate())
          if (modalPropertyAttributes.value.mode === "edit") {
            properties.value[modalPropertyAttributes.value.index].name =
              modalPropertyAttributes.value.name;
            properties.value[modalPropertyAttributes.value.index].description =
              modalPropertyAttributes.value.description || undefined;
          } else {
            const id = await controller.graph.database.generateColumnId();
            properties.value.push({
              id,
              name: modalPropertyAttributes.value.name,
              description:
                modalPropertyAttributes.value.description || undefined,
              permissions: { schema: "read_write" },
              origin: "table",
              type: boolDefinitionType.type,
              optional: false,
              list: modalPropertyAttributes.value.list,
            });
          }
        else return false;
      };

      const modalInstance = useModal({
        component: Modal,
        attrs: {
          title: getModalTitle("Boolean"),
          submitOnModEnter: true,
          modalButtons: [
            {
              title: "Done",
              kind: "main",
              func: handleSubmit,
            },
          ],
        },
        slots: {
          default: () =>
            h(CreateBaseProperty, {
              name: modalPropertyAttributes.value.name,
              description: modalPropertyAttributes.value.description,
              filled: modalPropertyAttributes.value.filled,
              optional: false,
              type: "Boolean",
              existingNames: existingPropertyNames.value,
              onUpdateName(t: string) {
                modalPropertyAttributes.value.name = t;
              },
              onUpdateDescription(d: string) {
                modalPropertyAttributes.value.description = d;
              },
              onSetNameFilled() {
                modalPropertyAttributes.value.filled = {
                  ...modalPropertyAttributes.value.filled,
                  name: true,
                };
              },
              async onSubmit() {
                const result = await handleSubmit();
                if (result !== false) {
                  modalInstance.close();
                }
              },
            }),
        },
      });

      return modalInstance;
    })();

    const { open: addIntProperty } = (() => {
      const handleSubmit = async () => {
        modalPropertyAttributes.value.filled = {
          ...modalPropertyAttributes.value.filled,
          name: !!modalPropertyAttributes.value.name,
        };
        if (modalPropertyAttributes.value.name && !isNameDuplicate())
          if (modalPropertyAttributes.value.mode === "edit") {
            properties.value[modalPropertyAttributes.value.index].name =
              modalPropertyAttributes.value.name;
            properties.value[modalPropertyAttributes.value.index].description =
              modalPropertyAttributes.value.description || undefined;
          } else {
            const id = await controller.graph.database.generateColumnId();
            properties.value.push({
              id,
              name: modalPropertyAttributes.value.name,
              description:
                modalPropertyAttributes.value.description || undefined,
              permissions: { schema: "read_write" },
              origin: "table",
              optional: modalPropertyAttributes.value.optional,
              type: numberDefinitionType.type,
              list: modalPropertyAttributes.value.list,
            });
          }
        else return false;
      };

      const modalInstance = useModal({
        component: Modal,
        attrs: {
          title: getModalTitle("Number"),
          submitOnModEnter: true,
          modalButtons: [
            {
              title: "Done",
              kind: "main",
              func: handleSubmit,
            },
          ],
        },
        slots: {
          default: () =>
            h(CreateBaseProperty, {
              name: modalPropertyAttributes.value.name,
              description: modalPropertyAttributes.value.description,
              filled: modalPropertyAttributes.value.filled,
              optional: modalPropertyAttributes.value.optional,
              type: "Number",
              existingNames: existingPropertyNames.value,
              onUpdateName(t: string) {
                modalPropertyAttributes.value.name = t;
              },
              onUpdateDescription(d: string) {
                modalPropertyAttributes.value.description = d;
              },
              onUpdateOptional(o: boolean) {
                modalPropertyAttributes.value.optional = o;
              },
              onSetNameFilled() {
                modalPropertyAttributes.value.filled = {
                  ...modalPropertyAttributes.value.filled,
                  name: true,
                };
              },
              async onSubmit() {
                const result = await handleSubmit();
                if (result !== false) {
                  modalInstance.close();
                }
              },
            }),
        },
      });

      return modalInstance;
    })();

    const { open: addDateProperty } = (() => {
      const handleSubmit = async () => {
        modalPropertyAttributes.value.filled = {
          ...modalPropertyAttributes.value.filled,
          name: !!modalPropertyAttributes.value.name,
        };
        if (modalPropertyAttributes.value.name && !isNameDuplicate())
          if (modalPropertyAttributes.value.mode === "edit") {
            properties.value[modalPropertyAttributes.value.index].name =
              modalPropertyAttributes.value.name;
            properties.value[modalPropertyAttributes.value.index].description =
              modalPropertyAttributes.value.description || undefined;
          } else {
            const id = await controller.graph.database.generateColumnId();
            properties.value.push({
              id,
              name: modalPropertyAttributes.value.name,
              description:
                modalPropertyAttributes.value.description || undefined,
              permissions: { schema: "read_write" },
              origin: "table",
              optional: modalPropertyAttributes.value.optional,
              type: dateDefinitionType.type,
              list: modalPropertyAttributes.value.list,
            });
          }
        else return false;
      };

      const modalInstance = useModal({
        component: Modal,
        attrs: {
          title: getModalTitle("Date"),
          submitOnModEnter: true,
          modalButtons: [
            {
              title: "Done",
              kind: "main",
              func: handleSubmit,
            },
          ],
        },
        slots: {
          default: () =>
            h(CreateBaseProperty, {
              name: modalPropertyAttributes.value.name,
              description: modalPropertyAttributes.value.description,
              filled: modalPropertyAttributes.value.filled,
              optional: modalPropertyAttributes.value.optional,
              type: "Date",
              existingNames: existingPropertyNames.value,
              onUpdateName(t: string) {
                modalPropertyAttributes.value.name = t;
              },
              onUpdateDescription(d: string) {
                modalPropertyAttributes.value.description = d;
              },
              onUpdateOptional(o: boolean) {
                modalPropertyAttributes.value.optional = o;
              },
              onSetNameFilled() {
                modalPropertyAttributes.value.filled = {
                  ...modalPropertyAttributes.value.filled,
                  name: true,
                };
              },
              async onSubmit() {
                const result = await handleSubmit();
                if (result !== false) {
                  modalInstance.close();
                }
              },
            }),
        },
      });

      return modalInstance;
    })();

    const { open: addSelectProperty } = useModal({
      component: Modal,
      attrs: {
        get title() {
          return getModalTitle("Select");
        },
        submitOnModEnter: true,
        modalButtons: [
          {
            title: "Done",
            kind: "main",
            func: async () => {
              modalPropertyAttributes.value.filled = {
                ...modalPropertyAttributes.value.filled,
                name: !!modalPropertyAttributes.value.name,
              };
              if (modalPropertyAttributes.value.name && !isNameDuplicate())
                if (modalPropertyAttributes.value.mode === "edit") {
                  properties.value[modalPropertyAttributes.value.index].name =
                    modalPropertyAttributes.value.name;
                  properties.value[
                    modalPropertyAttributes.value.index
                  ].description =
                    modalPropertyAttributes.value.description || undefined;
                  properties.value[modalPropertyAttributes.value.index].type = {
                    type: "variant",
                    cases: modalPropertyAttributes.value.selectOptions || [],
                  };
                  properties.value[modalPropertyAttributes.value.index].list =
                    modalPropertyAttributes.value.list;
                  stageSelectColors(
                    properties.value[modalPropertyAttributes.value.index].id
                  );
                } else {
                  const id = await controller.graph.database.generateColumnId();
                  properties.value.push({
                    id,
                    name: modalPropertyAttributes.value.name,
                    description:
                      modalPropertyAttributes.value.description || undefined,
                    permissions: { schema: "read_write" },
                    origin: "table",
                    type: {
                      type: "variant",
                      cases: modalPropertyAttributes.value.selectOptions || [],
                    },
                    optional: modalPropertyAttributes.value.optional,
                    list: modalPropertyAttributes.value.list,
                  });
                  stageSelectColors(id);
                }
              else return false;
            },
          },
        ],
      },
      slots: {
        default: () =>
          h(CreateSelect, {
            name: modalPropertyAttributes.value.name,
            description: modalPropertyAttributes.value.description,
            filled: modalPropertyAttributes.value.filled,
            options: modalPropertyAttributes.value.selectOptions,
            colors: modalPropertyAttributes.value.selectColors,
            optional: modalPropertyAttributes.value.optional,
            list: modalPropertyAttributes.value.list,
            existingNames: existingPropertyNames.value,
            onUpdateOptions(opts: Array<GraphCustomType>) {
              modalPropertyAttributes.value.selectOptions = opts;
            },
            onUpdateName(t: string) {
              modalPropertyAttributes.value.name = t;
            },
            onUpdateDescription(d: string) {
              modalPropertyAttributes.value.description = d;
            },
            onUpdateOptional(o: boolean) {
              modalPropertyAttributes.value.optional = o;
            },
            onSetNameFilled() {
              modalPropertyAttributes.value.filled = {
                ...modalPropertyAttributes.value.filled,
                name: true,
              };
            },
            onUpdateList(l: boolean) {
              modalPropertyAttributes.value.list = l;
            },
          }),
      },
    });

    const { open: addRelationProperty } = useModal({
      component: Modal,
      attrs: {
        title: getModalTitle("Relation"),
        submitOnModEnter: true,
        modalButtons: [
          {
            title: "Done",
            kind: "main",
            func: async () => {
              modalPropertyAttributes.value.filled = {
                ...modalPropertyAttributes.value.filled,
                name: !!modalPropertyAttributes.value.name,
                relation: !!modalPropertyAttributes.value.relation,
              };
              if (
                modalPropertyAttributes.value.name &&
                modalPropertyAttributes.value.relation &&
                !isNameDuplicate()
              )
                if (modalPropertyAttributes.value.mode === "edit") {
                  properties.value[modalPropertyAttributes.value.index].name =
                    modalPropertyAttributes.value.name;
                  properties.value[
                    modalPropertyAttributes.value.index
                  ].description =
                    modalPropertyAttributes.value.description || undefined;
                  properties.value[modalPropertyAttributes.value.index].type = {
                    type: "relation",
                    table: modalPropertyAttributes.value.relation.table,
                  };
                  properties.value[modalPropertyAttributes.value.index].list =
                    modalPropertyAttributes.value.list;
                } else {
                  const id = await controller.graph.database.generateColumnId();
                  properties.value.push({
                    id,
                    name: modalPropertyAttributes.value.name,
                    description:
                      modalPropertyAttributes.value.description || undefined,
                    permissions: { schema: "read_write" },
                    origin: "table",
                    type: {
                      type: "relation",
                      table: modalPropertyAttributes.value.relation.table,
                    },
                    optional: modalPropertyAttributes.value.optional,
                    list: modalPropertyAttributes.value.list,
                  });
                }
              else return false;
            },
          },
        ],
      },
      slots: {
        default: () =>
          h(CreateRelation, {
            name: modalPropertyAttributes.value.name,
            description: modalPropertyAttributes.value.description,
            filled: modalPropertyAttributes.value.filled,
            relation: modalPropertyAttributes.value.relation?.table,
            optional: modalPropertyAttributes.value.optional,
            list: modalPropertyAttributes.value.list,
            workspaceId: currentWorkspaceId.value,
            targetDatabases: relationTargets.value,
            existingNames: existingPropertyNames.value,
            onUpdateRelation(database: TableId | undefined) {
              modalPropertyAttributes.value.relation = database
                ? { table: database }
                : undefined;
            },
            onUpdateName(t: string) {
              modalPropertyAttributes.value.name = t;
            },
            onUpdateDescription(d: string) {
              modalPropertyAttributes.value.description = d;
            },
            onUpdateOptional(o: boolean) {
              modalPropertyAttributes.value.optional = o;
            },
            onSetNameFilled() {
              modalPropertyAttributes.value.filled = {
                ...modalPropertyAttributes.value.filled,
                name: true,
              };
            },
            onSetRelationFilled() {
              modalPropertyAttributes.value.filled = {
                ...modalPropertyAttributes.value.filled,
                relation: true,
              };
            },
            onUpdateList(l: boolean) {
              modalPropertyAttributes.value.list = l;
            },
          }),
      },
    });

    const { open: addNotesProperty } = (() => {
      const handleSubmit = async () => {
        modalPropertyAttributes.value.filled = {
          ...modalPropertyAttributes.value.filled,
          name: !!modalPropertyAttributes.value.name,
        };
        if (modalPropertyAttributes.value.name && !isNameDuplicate())
          if (modalPropertyAttributes.value.mode === "edit") {
            properties.value[modalPropertyAttributes.value.index].name =
              modalPropertyAttributes.value.name;
            properties.value[modalPropertyAttributes.value.index].description =
              modalPropertyAttributes.value.description || undefined;
          } else {
            const id = await controller.graph.database.generateColumnId();
            properties.value.push({
              id,
              name: modalPropertyAttributes.value.name,
              description:
                modalPropertyAttributes.value.description || undefined,
              permissions: { schema: "read_write" },
              origin: "table",
              type: notesDefinitionType.type,
              optional: modalPropertyAttributes.value.optional,
              list: false,
            });
          }
        else return false;
      };

      const modalInstance = useModal({
        component: Modal,
        attrs: {
          title:
            modalPropertyAttributes.value.mode === "edit"
              ? "Edit Notes"
              : "Create Notes",
          submitOnModEnter: true,
          modalButtons: [
            {
              title: "Done",
              kind: "main",
              func: handleSubmit,
            },
          ],
        },
        slots: {
          default: () =>
            h(CreateBaseProperty, {
              name: modalPropertyAttributes.value.name,
              description: modalPropertyAttributes.value.description,
              filled: modalPropertyAttributes.value.filled,
              optional: modalPropertyAttributes.value.optional,
              type: "Notes",
              existingNames: existingPropertyNames.value,
              onUpdateName(t: string) {
                modalPropertyAttributes.value.name = t;
              },
              onUpdateDescription(d: string) {
                modalPropertyAttributes.value.description = d;
              },
              onUpdateOptional(o: boolean) {
                modalPropertyAttributes.value.optional = o;
              },
              onSetNameFilled() {
                modalPropertyAttributes.value.filled = {
                  ...modalPropertyAttributes.value.filled,
                  name: true,
                };
              },
              onUpdateList(l: boolean) {
                modalPropertyAttributes.value.list = l;
              },
              async onSubmit() {
                const result = await handleSubmit();
                if (result !== false) {
                  modalInstance.close();
                }
              },
            }),
        },
      });

      return modalInstance;
    })();

    const titleFieldId = ref<string | undefined>();
    const notesFieldId = ref<string | undefined>();
    let notesFieldInitialized = false;
    const hiddenColumns = ref<Set<string>>(new Set());
    let hiddenColumnsInitialized = false;

    watch(
      [database, properties],
      ([database, props]) => {
        if (titleFieldId.value) return;
        titleFieldId.value =
          getTitleFieldId(database) ?? getTitleFieldIdFromProperties(props);
      },
      { immediate: true }
    );

    watch(
      database,
      (database) => {
        if (notesFieldInitialized) return;
        notesFieldId.value = database?.meta?.notes_field ?? undefined;
        if (database) notesFieldInitialized = true;
      },
      { immediate: true }
    );

    watch(
      database,
      (database) => {
        if (hiddenColumnsInitialized) return;
        if (database?.meta?.hidden_columns) {
          hiddenColumns.value = new Set(database.meta.hidden_columns);
          hiddenColumnsInitialized = true;
        }
      },
      { immediate: true }
    );

    const setTitleField = (index: number) => {
      const property = properties.value[index];
      if (property) {
        titleFieldId.value = property.id;
        // The title property is always displayed: promoting a hidden
        // property to title un-hides it.
        if (hiddenColumns.value.has(property.id)) {
          const next = new Set(hiddenColumns.value);
          next.delete(property.id);
          hiddenColumns.value = next;
          hiddenColumnsInitialized = true;
        }
      }
    };

    const toggleNotesField = (index: number) => {
      const property = properties.value[index];
      if (!property) return;
      notesFieldId.value =
        notesFieldId.value === property.id ? undefined : property.id;
      notesFieldInitialized = true;
    };

    const toggleHiddenColumn = (index: number) => {
      const property = properties.value[index];
      if (!property) return;
      const next = new Set(hiddenColumns.value);
      if (next.has(property.id)) next.delete(property.id);
      else next.add(property.id);
      hiddenColumns.value = next;
      hiddenColumnsInitialized = true;
    };

    const databaseMetadata = computed<DatabaseMetadata>(() => ({
      emoji: selectedEmoji.value || database.value?.meta?.emoji || undefined,
      icon: database.value?.meta?.icon || undefined,
      color: database.value?.meta?.color,
      title_field: titleFieldId.value,
      notes_field: notesFieldId.value,
      hidden_columns: Array.from(hiddenColumns.value),
    }));

    const typeToUpdate = computed<GraphCustomType>(() => ({
      type: "record",
      fields: properties.value.reduce(
        (record: GraphCustomTypeFieldDictionary, property: PropertyType) =>
          property.name && property.origin == "table"
            ? Object.assign(record, {
                [property.id]: property.list
                  ? {
                      name: property.name,
                      ...(property.description
                        ? { description: property.description }
                        : {}),
                      value: {
                        type: "list",
                        elements:
                          // FIXME Remove the check on relation when the backend allows
                          // for creating non-optional relations
                          property.optional && property.type.type !== "relation"
                            ? {
                                type: "variant",
                                cases: [property.type, { type: "null" }],
                              }
                            : property.type,
                      },
                    }
                  : {
                      name: property.name,
                      ...(property.description
                        ? { description: property.description }
                        : {}),
                      value:
                        // FIXME Remove the check on relation when the backend allows
                        // for creating non-optional relations
                        property.optional && property.type.type !== "relation"
                          ? {
                              type: "variant",
                              cases: [property.type, { type: "null" }],
                            }
                          : property.type,
                    },
              })
            : record,
        {}
      ),
    }));

    // The Task/Page/Project types provisioned in org workspaces are
    // designated uniquely per workspace (`provisionedKindOf`): an edit that
    // would make this type no longer the designated one — or deleting it —
    // would silently disconnect the screens and task binding relying on
    // it. Deletion is prevented by hiding the Delete button
    // (`guardedTypeKind`); edits are checked when the user clicks Save
    // (`checkGuardedType`). A second type named Task is not guarded.
    //
    // The personal workspace has no provisioned Task/Page: its hardcoded
    // built-in tables stand in for them, recognized by table id
    // (`builtinTypeKindOf`) — a custom type named Page or Task there is an
    // ordinary type — and get the same save check against the extension
    // columns the app reads by name (`matchesBuiltinTypeSchema`: the task
    // Labels, Priority and Project, the page/event/people Labels, the
    // Labels table's Title). Like a provisioned type that no
    // longer matches its base schema is not designated — hence not guarded
    // until repaired — a built-in table that does not match (e.g. a Task
    // whose Project relation the Projects repair never added) is left
    // unguarded rather than having every edit refused; its Delete button
    // is withheld regardless (`isProtectedBuiltinType`). The personal
    // workspace's Project type is provisioned and designated like an org
    // one.
    //
    // The edit check runs deliberately outside any reactive context: it
    // matches against the edition draft, whose property modals mutate
    // objects shared by reference with `properties`, so a computed doing
    // this would re-run (and re-render the page) on every keystroke.
    // `guardedTypeKind` only reads the saved schemas from the controller,
    // which the draft never mutates, so it can safely be a computed: it
    // re-evaluates on controller updates only, not while typing.
    const currentWorkspaceDatabases = (): GraphDatabaseDictionary =>
      workspaceDatabases.value?.[currentWorkspaceId.value] ?? {};

    type GuardedTypeKind =
      | { origin: "provisioned"; kind: ProvisionedTypeKind }
      | { origin: "builtin"; kind: BuiltinTypeKind };

    // The personal workspace's fixed tables, looked up by id by the app:
    // never deletable, whatever their current schema.
    const isProtectedBuiltinType = computed(
      () =>
        database.value !== undefined && isProtectedBuiltinTable(database.value)
    );

    // The kind the saved type is guarded as, if any (see the comment above
    // for why this may be a computed). Hides the Delete button: deleting a
    // provisioned type would break the Tasks/Pages/Projects screens until
    // the user goes through their Repair empty state, which recreates it.
    const guardedTypeKind = computed<GuardedTypeKind | undefined>(() => {
      // No kind means no guard: the Delete button stays visible and
      // `checkGuardedType` accepts any edit.
      if (OVERRIDE_TYPE_EDITION) return undefined;
      if (mode.value !== "edition") return undefined;
      const db = database.value;
      if (!db) return undefined;
      const builtin = builtinTypeKindOf(db);
      if (builtin) {
        // Built-in tables are never provisioned candidates: no fallthrough.
        return matchesBuiltinTypeSchema(db, builtin)
          ? { origin: "builtin", kind: builtin }
          : undefined;
      }
      const provisioned = provisionedKindOf(db, currentWorkspaceDatabases());
      return provisioned
        ? { origin: "provisioned", kind: provisioned }
        : undefined;
    });

    // The edited type as it would be saved, shaped as a GraphDatabase so
    // designation can be re-run on the workspace with this draft in place.
    const editedDatabase = (): GraphDatabase | undefined => {
      const db = database.value;
      const edited = typeToUpdate.value;
      if (!db || edited.type !== "record") return undefined;
      const fields: FieldWithOriginDictionary = {};
      // Virtual fields are synthesized by the controller: they are not part
      // of the update payload (`typeToUpdate` only carries table-backed
      // properties) but survive the save unchanged, so the draft must keep
      // them — else the check would see them as deleted.
      for (const [fieldId, field] of Object.entries(db.type.fields)) {
        if (field.origin === "virtual") fields[fieldId] = field;
      }
      for (const [fieldId, field] of Object.entries(edited.fields)) {
        fields[fieldId] = { ...field, origin: "table" };
      }
      return {
        ...db,
        data_name: typeTitle.value,
        name: databaseInput.value,
        meta: databaseMetadata.value,
        type: { type: "record", fields },
      };
    };

    const provisionedTypeRequirements: Record<ProvisionedTypeKind, string> = {
      Task: 'stay named "Task" and keep its provisioned properties: Title (as the title property), Notes, a Status offering the Todo, Completed and Discarded options, Planned For, Assignee, Project and Context',
      Page: 'stay named "Page" and keep Title (as the title property) and Notes',
      Project:
        'stay named "Project" and keep Title (as the title property) and Notes',
    };
    const builtinTypeRequirements: Record<BuiltinTypeKind, string> = {
      task: "keep its Labels relation, Priority select and Project relation properties",
      page: "keep its Labels relation property",
      event: "keep its Labels relation property",
      people: "keep its Labels relation property",
      label: "keep Title (as the title property)",
    };

    // Whether the draft keeps the type intact for its guarded kind: still
    // designated as the same provisioned kind once saved in place of the
    // current type, or still carrying the built-in kind's required columns.
    const guardedTypeIntact = (
      guard: GuardedTypeKind,
      db: GraphDatabase,
      edited: GraphDatabase
    ): boolean => {
      if (guard.origin === "builtin") {
        return matchesBuiltinTypeSchema(edited, guard.kind);
      }
      const next: GraphDatabaseDictionary = {
        ...currentWorkspaceDatabases(),
        [db.id]: edited,
      };
      return provisionedKindOf(edited, next) === guard.kind;
    };

    const checkGuardedType = (): string | null => {
      if (mode.value !== "edition") return null;
      const guard = guardedTypeKind.value;
      const db = database.value;
      const edited = editedDatabase();
      if (!guard || !db || !edited) return null;
      if (guardedTypeIntact(guard, db, edited)) return null;
      return guard.origin === "builtin"
        ? `This type supports your ${pluralize(guard.kind)}: it must ${
            builtinTypeRequirements[guard.kind]
          }. Revert these changes to save.`
        : `This type supports workspace's ${pluralize(
            guard.kind.toLowerCase()
          )}: it must ${
            provisionedTypeRequirements[guard.kind]
          }. Revert these changes to save.`;
    };

    const guardedTypeError = ref<string | null>(null);

    const nameError = ref<string | null>(null);
    const propertyError = ref<string | null>(null);

    // The saved schema can hold neither the variant case order (CRDT merges
    // can reorder it) nor the color picked for each option, so both are
    // snapshotted to the browser store on every save. Views grouped by the
    // select use the order as their default group order (see View.vue), and
    // the chips rendered by GraphSelect/BaseGroupedBy use the colors.
    // Colors submitted from the select modal (keyed by column id), staged
    // until the type is saved. Reactive so the dirty check (which compares
    // effective colors, see `effectiveSelectColors`) sees color-only edits.
    const pendingSelectColors = reactive(
      new Map<string, Partial<Record<string, CalendarColor>>>()
    );
    // The colors a select column currently shows, one entry per colored
    // option: the colors staged this session (or, before any restaging, the
    // persisted ones) overlaid on the provisioned defaults — the same
    // resolution `editProperty` uses to fill the modal, so submitting the
    // modal without touching a color leaves the snapshot unchanged.
    const effectiveSelectColors = (
      property: PropertyType
    ): Array<[option: string, color: CalendarColor]> => {
      const type = extractOptionalFromCustom(property.type);
      if (type.type !== "variant") return [];
      const colors = {
        ...(database.value
          ? provisionedSelectConfig(database.value, property.name)?.colors
          : undefined),
        ...(pendingSelectColors.get(property.id) ??
          (currentWorkspaceId.value && id.value
            ? storedSelectConfig(
                browserStore,
                currentWorkspaceId.value,
                id.value,
                property.id
              )?.colors
            : undefined)),
      };
      return type.cases.flatMap((option) => {
        if (option.type !== "string_const") return [];
        const color = colors[option.value];
        return color
          ? [[option.value, color] satisfies [string, CalendarColor]]
          : [];
      });
    };
    const stageSelectColors = (columnId: string) => {
      const colors: Partial<Record<string, CalendarColor>> = {};
      for (const option of modalPropertyAttributes.value.selectOptions) {
        if (option.type !== "string_const") continue;
        const color = modalPropertyAttributes.value.selectColors.get(option);
        if (color) colors[option.value] = color;
      }
      pendingSelectColors.set(columnId, colors);
    };
    const collectSelectOrders = (): Array<[columnId: string, string[]]> => {
      const orders: Array<[string, string[]]> = [];
      for (const property of properties.value) {
        if (property.origin !== "table") continue;
        const propType = property.type;
        if (propType.type !== "variant") continue;
        const cases = propType.cases;
        if (!cases.some((c) => c.type === "string_const")) continue;
        if (!cases.every((c) => c.type === "string_const" || c.type === "null"))
          continue;
        orders.push([
          property.id,
          cases.flatMap((c) => (c.type === "string_const" ? [c.value] : [])),
        ]);
      }
      return orders;
    };
    const persistSelectConfigs = (
      tableId: string,
      orders: Array<[columnId: string, string[]]>
    ) => {
      const wsId = currentWorkspaceId.value;
      if (!wsId) return;
      for (const [columnId, order] of orders) {
        // Columns whose select modal was not opened this session keep the
        // colors they had.
        const colors =
          pendingSelectColors.get(columnId) ??
          storedSelectConfig(browserStore, wsId, tableId, columnId)?.colors ??
          {};
        persistSelectConfig(browserStore, wsId, tableId, columnId, {
          order,
          colors,
        });
      }
    };

    const validate = async () => {
      guardedTypeError.value = checkGuardedType();
      if (guardedTypeError.value) return;
      if (typeTitle.value && typeToUpdate.value && currentWorkspaceId.value) {
        nameError.value = null; // Clear previous errors

        // Snapshotted before the save: the controller update triggers the
        // deep [types, mode] watch above, which replaces `properties` with
        // the reloaded schema (in CRDT case order) before this function
        // resumes past the await.
        const selectOrders = collectSelectOrders();

        let result;
        if (mode.value === "creation") {
          result = await controller.graph.database.add(
            currentWorkspaceId.value,
            typeTitle.value,
            databaseNameToUpdate.value ||
              databaseNameSuggestion.value ||
              "untitled-type",
            databaseMetadata.value,
            typeToUpdate.value,
            typeDescription.value
          );
        } else if (mode.value === "edition" && id.value) {
          result = await controller.graph.database.update(
            currentWorkspaceId.value,
            id.value,
            typeTitle.value,
            databaseNameToUpdate.value ||
              databaseNameSuggestion.value ||
              "untitled-type",
            databaseMetadata.value,
            typeToUpdate.value,
            typeDescription.value
          );
        }

        // Handle the result using pattern matching
        if (result && "error" in result) {
          nameError.value = match(result.error.error_kind)
            .with(
              "duplicate_data_name",
              () => "A type with this name already exists in this workspace"
            )
            .with(
              "not_a_record_type",
              () => "Custom types must be a record type."
            )
            .with(
              "workspace_not_found",
              () => "This workspace is no longer available."
            )
            .with(
              "workspace_write_forbidden",
              () => "You don't have permission to modify this workspace."
            )
            .exhaustive();
          return; // Don't navigate away if there's an error
        }

        const savedTableId =
          mode.value === "edition"
            ? id.value
            : result && "value" in result
              ? result.value
              : undefined;
        if (savedTableId) {
          persistSelectConfigs(savedTableId, selectOrders);
          persistCurrentPropertyOrder(savedTableId);
        }

        // Only navigate if there's no error
        skipRouteGuard.value = true;
        navigateTo(RouteNames.SettingsTypes, router, {
          params: { orgId: orgId.value, workspaceId: currentWorkspaceId.value },
        });
      }
    };

    const deleteType = async () => {
      if (!id.value) return;
      const confirmed = await requestDeleteConfirmation(
        simpleModal,
        DELETE_TYPE_CONFIRMATION_COPY
      );
      if (!confirmed) return;
      skipRouteGuard.value = true;
      await navigateTo(RouteNames.SettingsTypes, router, {
        params: {
          orgId: orgId.value,
          workspaceId: currentWorkspaceId.value,
        },
      });
      if (id.value && currentWorkspaceId.value)
        controller.graph.database.remove(currentWorkspaceId.value, id.value);
    };

    // Setup Tippy for emoji picker
    const iconContainer = ref<HTMLElement>();
    const emojiPickerContent = ref<HTMLElement>();

    const focusEmojiPickerSearch = () => {
      nextTick(() => {
        requestAnimationFrame(() => {
          const root = emojiPickerContent.value;
          if (!root) return;
          const input =
            root.querySelector<HTMLInputElement>(
              ".emoji-mart-search input, .emoji-mart input[type='text']"
            ) ?? root.querySelector<HTMLInputElement>("input[type='search']");
          input?.focus();
        });
      });
    };

    const emojiPickerTippy = useTippy(iconContainer, {
      placement: "bottom-start",
      animateFill: false,
      appendTo: () => document.body,
      content: () => emojiPickerContent.value,
      duration: 100,
      interactive: true,
      trigger: "manual",
      arrow: true,
      theme: "menu",
      offset: [0, 8],
      onShown: focusEmojiPickerSearch,
    });

    const emojiPickerMenuShown = computed(
      () => emojiPickerTippy.state.value.isShown
    );

    onBeforeUnmount(() => emojiPickerTippy.destroy());

    const onEmojiSelect = (emoji: string) => {
      selectedEmoji.value = emoji;
      emojiPickerTippy.hide();
    };

    const onEmojiPickerClose = () => {
      emojiPickerTippy.hide();
    };

    const onIconClick = () => {
      if (emojiPickerTippy.state.value.isShown) {
        emojiPickerTippy.hide();
      } else {
        emojiPickerTippy.show();
      }
    };

    const hasSystemProperties = computed<boolean>(() => {
      return properties.value.some((property) => property.origin !== "table");
    });

    const copyTableId = async () => {
      if (id.value) {
        await copy(id.value);
        controller.notifications.notify("Table ID copied to clipboard");
      }
    };

    const cancel = () => {
      skipRouteGuard.value = true;
      navigateTo(RouteNames.SettingsTypes, router, {
        params: { orgId: orgId.value, workspaceId: currentWorkspaceId.value },
      });
    };

    return {
      cancel,
      copyTableId,
      types,
      typeToUpdate,
      databaseMetadata,
      deleteType,
      createProperty,
      addPropertyMenuList,
      addPropertyMenuRowById,
      onAddPropertyMenuSelect,
      TicketIcon,
      database,
      databaseInput,
      deleteProperty,
      editProperty,
      emojiPickerContent,
      emojiPickerMenuShown,
      getTypeColor,
      hasSystemProperties,
      iconContainer,
      isContextProperty,
      isDirty,
      isIntegrationDatabase,
      mode,
      duplicateFieldNames,
      onIconClick,
      onEmojiPickerClose,
      onEmojiSelect,
      properties,
      propertySections,
      onPropertiesReorder,
      OVERRIDE_TYPE_EDITION,
      readonlySchema,
      readonlyType,
      canDeleteType,
      saveButtonState,
      selectedEmoji,
      defaultTypeIcon,
      setTitleField,
      toggleNotesField,
      notesFieldId,
      hiddenColumns,
      toggleHiddenColumn,
      titleFieldId,
      titleInputEl,
      typeDescription,
      typeTitle,
      nameError,
      propertyError,
      guardedTypeError,
      guardedTypeKind,
      isProtectedBuiltinType,
      validate,
    };
  },
});
</script>
<style lang="scss" scoped>
@use "../../styles/settings.scss";

.type-edition {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
  width: 100%;

  .title-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    column-gap: 8px;

    .table-id {
      display: flex;
      align-items: center;
      gap: 4px;
      margin-inline-start: 24px;
      @extend %font-small-regular;

      @include themed() {
        color: t($grey-40);
      }

      .copy-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: none;
        border: none;
        padding: 2px;
        opacity: 0.7;
        @include transition(opacity 0.2s ease-out);

        &:hover {
          opacity: 1;
        }

        svg {
          width: 12px;
          height: 12px;
        }
      }
    }
  }

  h2 {
    margin-bottom: 0;
  }

  .content {
    display: flex;
    flex-direction: column;
    row-gap: 32px;

    :deep(.column-1) {
      width: 185px;
    }

    :deep(.column-2) {
      min-width: 130px;
    }

    :deep(.column-3) {
      flex: 1;
    }

    .schema-tab {
      display: flex;
      flex-direction: column;
      row-gap: 32px;
    }

    .general {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      column-gap: 32px;
      width: 100%;

      .names {
        display: flex;
        flex-direction: column;
        row-gap: 16px;
        width: 100%;
      }
    }

    .properties {
      display: flex;
      flex-direction: column;

      .handle-spacer,
      .handle {
        width: 20px;
        flex-shrink: 0;
      }

      .handle {
        cursor: grab;
      }

      .property-row {
        display: flex;
        flex-direction: row;
        align-items: center;
        column-gap: 8px;
        width: 100%;

        :deep(.definition) {
          flex: 1;
          min-width: 0;
          width: auto;
        }

        :deep(.handle svg) {
          width: 14px;
          height: 14px;
        }
      }

      .column-headers {
        display: flex;
        flex-direction: row;
        column-gap: 8px;
        padding: 8px;
        padding-inline-end: 48px;
        @extend %font-h5;

        @include themed() {
          color: t($grey-40);
          border-bottom: 1px solid t($grey-10);
        }

        .column-1 {
          min-width: 185px;
        }

        .column-2 {
          min-width: 130px;
        }

        .column-3 {
          flex: 1;
        }
      }

      button.base-button.none.auxiliary.add {
        margin-top: 12px;
      }
    }
  }

  .buttons {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    .buttons-left {
      display: flex;
      flex-direction: row;
      column-gap: 8px;
    }

    .buttons-right {
      display: flex;
      flex-direction: row;
      column-gap: 8px;
    }
  }
}
</style>
<style lang="scss">
/* Unscoped style to hide emoji picker in its original position */
/* This won't interfere with Tippy's display of the moved element */
.type-edition .emoji-picker-container {
  display: none;
}
</style>
